import React, { useMemo, useState } from 'react';
import { sendIcon } from '../chatIcons';
import './TextInput.css';

export type TextInputFieldType = 'text' | 'tel' | 'email' | 'date';

export interface TextInputField {
  id: string;
  label: string;
  /** When true, label is not shown (question is in chat); use ariaLabel for accessibility. */
  hideLabel?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  autoComplete?: string;
  type?: TextInputFieldType;
  required?: boolean;
  maxLength?: number;
  /** Return an error message when invalid; return null/undefined when valid. */
  validate?: (value: string) => string | null | undefined;
}

export type TextInputValues = Record<string, string>;

export interface TextInputProps {
  fields: TextInputField[];
  values: TextInputValues;
  onChange: (id: string, value: string) => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
  /** @deprecated unused; send icon only */
  footerLeftLabel?: string;
  submitAriaLabel?: string;
  requiredMessage?: string;
}

const TextInput: React.FC<TextInputProps> = ({
  fields,
  values,
  onChange,
  onSubmit,
  submitDisabled,
  submitAriaLabel = 'Send',
  requiredMessage = 'Required',
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    for (const f of fields) {
      const raw = values[f.id] ?? '';
      const v = raw.trim();
      if (f.required && v.length === 0) {
        e[f.id] = 'required';
        continue;
      }
      if (v.length > 0 && f.validate) {
        const msg = f.validate(v);
        if (typeof msg === 'string' && msg.trim().length > 0) {
          e[f.id] = msg;
        }
      }
    }
    return e;
  }, [fields, values]);

  const hasErrors = Object.keys(errors).length > 0;
  const disabled = Boolean(submitDisabled) || hasErrors;

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (disabled) return;
    onSubmit();
  };

  return (
    <div className="ff-text-input">
      <div className="ff-text-input__fields">
        {fields.map((f) => (
          <div
            key={f.id}
            className={`ff-text-input__row${f.hideLabel ? ' ff-text-input__row--no-label' : ''}`}
          >
            <label
              className={`ff-text-input__field${f.hideLabel ? ' ff-text-input__field--no-label' : ''}`}
            >
              {!f.hideLabel && <span className="ff-text-input__label">{f.label}</span>}
              <input
                type={f.type ?? 'text'}
                className={`ff-text-input__input ${
                  (touched[f.id] || submitAttempted) && errors[f.id] ? 'ff-text-input__input--error' : ''
                }`}
                value={values[f.id] ?? ''}
                onChange={(e) => {
                  setTouched((prev) => (prev[f.id] ? prev : { ...prev, [f.id]: true }));
                  onChange(f.id, e.target.value);
                }}
                placeholder={f.placeholder}
                autoComplete={f.autoComplete ?? 'off'}
                required={Boolean(f.required)}
                maxLength={f.maxLength}
                aria-label={f.hideLabel ? f.ariaLabel ?? f.label : undefined}
                aria-invalid={Boolean((touched[f.id] || submitAttempted) && errors[f.id])}
              />
            </label>
            {(touched[f.id] || submitAttempted) && errors[f.id] && (
              <div className="ff-text-input__error" role="alert">
                {errors[f.id] === 'required' ? requiredMessage : errors[f.id]}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="ff-text-input__footer">
        <button
          type="button"
          className="ff-text-input__send"
          onClick={handleSubmit}
          disabled={disabled}
          aria-label={submitAriaLabel}
        >
          <img src={sendIcon} alt="" aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default TextInput;

